import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppStorage, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import useJaktdataHelper from "~/src/contexts/StorageContext/useJaktdataHelper";
import { sumSettOgSkuttJaktdag } from "~/src/helpers/jaktdagHelper";
import { hentJaktaar } from "~/src/helpers/time";
import { useClientState } from "~/src/hooks/useClientState";
import { Stateful } from "~/src/hooks/useStateful";
import { getJaktdagerQuery, getSkutteDyrQuery } from "~/src/react-query/queries";
import { IndividResponse, Jaktdag, JaktdagResponse } from "../../api/types";
import { FilterType } from "./Components/FilterCombobox";

export function isMissingOvervakData(skuttDyr?: IndividResponse) {
  if (!skuttDyr) {
    return false;
  }

  return skuttDyr.SkuttIOvervåkområde && (!skuttDyr.Merkelapp?.trim() || !skuttDyr.SlaktevektKg);
}

export function getReadableEnumLowercase(enumProp: string | null | undefined) {
  if (!enumProp) {
    return "";
  }
  return enumProp
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase();
}

export function getJaktdager(
  jaktomradeId: string | undefined,
  selectedFilters: FilterType[],
  storage: AppStorage,
  jegerNummer: number | undefined,
  showArtFilter: boolean | undefined,
  totalResultsJaktdager: Stateful<number>,
) {
  const jaktdagFilter = (
    response: StorageItem<JaktdagResponse>[],
    deps: FilterDependencies,
  ): StorageItem<JaktdagResponse>[] => {
    return response.filter((r) => matchesJaktdagFilters(r, deps));
  };
  const { updateJaktdagerFromStore } = useJaktdataHelper();
  const queryClient = useQueryClient();
  const clientState = useClientState();
  const isOnline = clientState === "Online";

  return useQuery({
    meta: { url: `/jaktdager/${jaktomradeId}/${hentJaktaar()}` },
    queryKey: [
      "jaktdager",
      {
        jaktomradeId: jaktomradeId || "",
        sesong: hentJaktaar(),
      },
    ],
    queryFn: async (context) => {
      try {
        const jaktdager = await getJaktdagerQuery(context);
        const queryItems = jaktdager.map((jaktdag): StorageItem<JaktdagResponse> => {
          return {
            objectKey: { id: jaktdag.Id },
            isPending: false,
            data: jaktdag,
          };
        });
        const display = updateJaktdagerFromStore(queryItems, jaktomradeId);
        totalResultsJaktdager.set(display.length);
        return display;
      } catch (_error) {
        const cache = queryClient.getQueryData([
          "jaktdager",
          {
            jaktomradeId: jaktomradeId,
            sesong: hentJaktaar(),
          },
        ]);
        const display = updateJaktdagerFromStore(
          (cache as StorageItem<JaktdagResponse>[]) ?? [],
          jaktomradeId,
        );
        totalResultsJaktdager.set(display.length);
        return display;
      }
    },
    enabled: isOnline && !!jaktomradeId,
    select: (response) =>
      jaktdagFilter(response, {
        storage,
        showArtFilter,
        selectedFilters: selectedFilters,
        jegerNummer,
      }),
    staleTime: 0,
    gcTime: Infinity,
  });
}

export function getSkutteDyr(
  jaktomradeId: string | undefined,
  selectedFilters: FilterType[],
  storage: AppStorage,
  jegerNummer: number | undefined,
  showArtFilter: boolean | undefined,
  totalResultsIndivider: Stateful<number>,
) {
  const skutteDyrFilter = (
    response: StorageItem<IndividResponse>[],
    deps: FilterDependencies,
  ): StorageItem<IndividResponse>[] => {
    return response.filter((r) => matchesIndividFilters(r, deps));
  };

  const { updateIndividerFromStore } = useJaktdataHelper();
  const queryClient = useQueryClient();
  const clientState = useClientState();
  const isOnline = clientState === "Online";

  return useQuery({
    meta: { url: `/individer/${jaktomradeId}/${hentJaktaar()}` },
    queryKey: [
      "skuttedyr",
      {
        jaktomradeId: jaktomradeId || "",
        sesong: hentJaktaar(),
      },
    ],
    queryFn: async (context) => {
      try {
        const skutteDyr = await getSkutteDyrQuery(context);
        const queryItems = skutteDyr.map((skuttDyr): StorageItem<IndividResponse> => {
          return {
            objectKey: { id: skuttDyr.Id },
            isPending: false,
            data: skuttDyr,
          };
        });
        const display = updateIndividerFromStore(queryItems, jaktomradeId);
        totalResultsIndivider.set(display.length);
        return display;
      } catch (_error) {
        const cache = queryClient.getQueryData([
          "skuttedyr",
          {
            jaktomradeId: jaktomradeId,
            sesong: hentJaktaar(),
          },
        ]);
        const display = updateIndividerFromStore(
          (cache as StorageItem<IndividResponse>[]) ?? [],
          jaktomradeId,
        );
        totalResultsIndivider.set(display.length);
        return display;
      }
    },
    enabled: isOnline && !!jaktomradeId,
    select: (response) =>
      skutteDyrFilter(response, {
        storage,
        showArtFilter,
        selectedFilters: selectedFilters,
        jegerNummer,
      }),
    staleTime: 0,
    gcTime: Infinity,
  });
}

type FilterDependencies = {
  showArtFilter?: boolean;
  selectedFilters: FilterType[];
  storage: AppStorage;
  jegerNummer?: number;
};

const matchesJaktdagFilters = (
  r: StorageItem<JaktdagResponse>,
  { showArtFilter, selectedFilters, storage, jegerNummer }: FilterDependencies,
): boolean => {
  const matchesArtFilter =
    !showArtFilter ||
    !(selectedFilters.includes(FilterType.Elg) || selectedFilters.includes(FilterType.Hjort)) ||
    (r.data.Art !== undefined &&
      r.data.Art !== null &&
      selectedFilters.includes(r.data.Art as FilterType));

  const matchesMineRegFilter =
    !selectedFilters.includes(FilterType.MineReg) || r.data.Jegernummer === jegerNummer;

  const matchesVarslerRegFilter = (() => {
    if (!selectedFilters.includes(FilterType.VarslerReg)) return true;
    const jaktdag = (storage && storage.setIndividerForJaktdag(r.data as Jaktdag)) || r.data;
    return r.data.UtenforJakttid || sumSettOgSkuttJaktdag(jaktdag).isInconsistent;
  })();

  return matchesArtFilter && matchesMineRegFilter && matchesVarslerRegFilter;
};

const matchesIndividFilters = (
  r: StorageItem<IndividResponse>,
  { showArtFilter, selectedFilters, jegerNummer }: FilterDependencies,
): boolean => {
  const matchesIndividArtFilter =
    !showArtFilter ||
    !(selectedFilters.includes(FilterType.Elg) || selectedFilters.includes(FilterType.Hjort)) ||
    (r.data.Art !== undefined &&
      r.data.Art !== null &&
      selectedFilters.includes(r.data.Art as FilterType));

  const matchesIndividMineRegFilter =
    !selectedFilters.includes(FilterType.MineReg) || r.data.Jegernummer === jegerNummer;

  const matchesIndividVarslerRegFilter = (() => {
    if (!selectedFilters.includes(FilterType.VarslerReg)) return true;
    return (r.data.UtenforJakttid || isMissingOvervakData(r.data)) ?? false;
  })();

  return matchesIndividArtFilter && matchesIndividMineRegFilter && matchesIndividVarslerRegFilter;
};
